import { difference } from 'lodash-es'

import { MAX_TAGS } from 'components/integrations/mailchimp/rules/editor/select/tags/constants'

const searchableState = {
  isLoading: false,
  data: [],
  selected: '',
  term: '',
  errors: []
}

const initialState = {
  account: {
    isLoading: true
  },
  eventTypes: {
    ...searchableState
  },
  audiences: {
    ...searchableState
  },
  tags: {
    ...searchableState,
    selected: []
  },
  rules: {
    isEditorOpen: false,
    isEditorLoading: false,
    isLoading: false,
    data: [],
    selected: null
  }
}

const resetSelection = (state) => ({
  ...state,
  eventTypes: {
    ...state.eventTypes,
    selected: null,
    errors: []
  },
  audiences: {
    ...state.audiences,
    selected: null,
    errors: []
  },
  tags: {
    ...state.tags,
    selected: [],
    term: ''
  },
  rules: {
    ...state.rules,
    selected: null
  }
})

const fetchCollection = (state) => (collection) => ({
  ...state,
  [collection]: {
    ...state[collection],
    isLoading: true
  }
})

const fetchSuccess = (state, payload) => (collection) => ({
  ...state,
  [collection]: {
    ...state[collection],
    data: payload[collection]
  }
})

const stopLoading = (state) => (collection) => ({
  ...state,
  [collection]: {
    ...state[collection],
    isLoading: false,
  }
})

const fetchError = (state) => (collection) => ({
  ...state,
  [collection]: {
    ...state[collection]
  }
})

const selectValue = (state, payload) => (collection) => ({
  ...state,
  [collection]: {
    ...state[collection],
    selected: payload.value,
    errors: []
  }
})

const filterCollection = (state, payload) => (collection) => ({
  ...state,
  [collection]: {
    ...state[collection],
    term: payload.term
  }
})

const deleteRule = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isDeleting: true
  }
})

const deleteRuleSuccess = (state, { id }) => ({
  ...state,
  rules: {
    ...state.rules,
    isDeleting: false,
    data: state.rules.data.filter((rule) => rule.id !== id)
  }
})

const deleteRuleError = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isDeleting: false
  }
})

const selectTag = (state, { tag }) => {
  const { tags: { data, selected } } = state

  if (selected.length === MAX_TAGS) {
    return state
  }

  return {
    ...state,
    tags: {
      ...state.tags,
      data: data.filter(({ id }) => id !== tag.id),
      selected: selected.concat(tag),
      term: ''
    }
  }
}

const selectTags = (state, { tags: tagsFromRule }) => {
  const availableTags = state.tags.data
  const remainingTagNames = difference(
    availableTags.map(({ name }) => name),
    tagsFromRule.map(({ name }) => name)
  )

  const remainingTags = availableTags.filter(
    ({ name }) => remainingTagNames.includes(name)
  )

  return {
    ...state,
    tags: {
      ...state.tags,
      data: remainingTags,
      selected: tagsFromRule,
      term: ''
    }
  }
}

const deselectTag = (state, { tag }) => {
  const { tags: { data, selected } } = state

  return {
    ...state,
    tags: {
      ...state.tags,
      data: data.concat(tag),
      selected: selected.filter(({ id }) => id !== tag.id),
      term: ''
    }
  }
}

const clearSelectedTags = (state) => ({
  ...state,
  tags: {
    ...state.tags,
    selected: []
  }
})

const selectRule = (state, { rule }) => ({
  ...state,
  rules: {
    ...state.rules,
    selected: rule
  }
})

const openRuleEditor = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorOpen: true
  }
})

const closeRuleEditor = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorOpen: false
  }
})

const ruleEditorIsLoading = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorLoading: true
  }
})

const ruleEditorLoadingComplete = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorLoading: false
  }
})

const ruleEditorIsSaving = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorSaving: true
  }
})

const ruleEditorSavingSuccess = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorSaving: false
  }
})

const ruleEditorSavingError = (state) => ({
  ...state,
  rules: {
    ...state.rules,
    isEditorSaving: false
  }
})

const setEventTypeErrors = (state, { errors }) => ({
  ...state,
  eventTypes: {
    ...state.eventTypes,
    isLoading: false,
    errors
  }
})

const setAudienceErrors = (state, { errors }) => ({
  ...state,
  audiences: {
    ...state.audiences,
    isLoading: false,
    errors
  }
})

const startAccountLoading = (state) => ({
  ...state,
  account: {
    ...state.account,
    isLoading: true
  }
})

const stopAccountLoading = (state) => ({
  ...state,
  account: {
    ...state.account,
    isLoading: false
  }
})

const accountLoaded = (state, {
  email,
  access_granted: accessGranted
}) => ({
  ...state,
  account: {
    ...state.account,
    email,
    accessGranted
  }
})

export default (state = initialState, { type, payload }) => {
  const fetch = fetchCollection(state)
  const success = fetchSuccess(state, payload)
  const error = fetchError(state)
  const select = selectValue(state, payload)
  const filter = filterCollection(state, payload)
  const loadingComplete = stopLoading(state)

  switch (type) {
    case 'MAILCHIMP_FETCH_EVENT_TYPES':
      return fetch('eventTypes')
    case 'MAILCHIMP_FETCH_EVENT_TYPES_SUCCESS':
      return success('eventTypes')
    case 'MAILCHIMP_FETCH_EVENT_TYPES_ERROR':
      return error('eventTypes')
    case 'MAILCHIMP_EVENT_TYPES_LOADING_COMPLETE':
      return loadingComplete('eventTypes')
    case 'MAILCHIMP_SELECT_EVENT_TYPE':
      return select('eventTypes')
    case 'MAILCHIMP_RESET_SELECTION':
      return resetSelection(state)
    case 'MAILCHIMP_FILTER_EVENT_TYPES':
      return filter('eventTypes')
    case 'MAILCHIMP_FETCH_AUDIENCES':
      return fetch('audiences')
    case 'MAILCHIMP_FETCH_AUDIENCES_SUCCESS':
      return success('audiences')
    case 'MAILCHIMP_AUDIENCES_LOADING_COMPLETE':
      return loadingComplete('audiences')
    case 'MAILCHIMP_SELECT_AUDIENCE':
      return select('audiences')
    case 'MAILCHIMP_FILTER_AUDIENCES':
      return filter('audiences')
    case 'MAILCHIMP_FETCH_TAGS':
      return fetch('tags')
    case 'MAILCHIMP_FETCH_TAGS_SUCCESS':
      return success('tags')
    case 'MAILCHIMP_TAGS_LOADING_COMPLETE':
      return loadingComplete('tags')
    case 'MAILCHIMP_SELECT_TAG':
      return selectTag(state, payload)
    case 'MAILCHIMP_SELECT_TAGS':
      return selectTags(state, payload)
    case 'MAILCHIMP_DESELECT_TAG':
      return deselectTag(state, payload)
    case 'MAILCHIMP_FILTER_TAGS':
      return filter('tags')
    case 'MAILCHIMP_FETCH_RULES':
      return fetch('rules')
    case 'MAILCHIMP_FETCH_RULES_SUCCESS':
      return success('rules')
    case 'MAILCHIMP_FETCH_RULES_ERROR':
      return error('rules')
    case 'MAILCHIMP_RULES_LOADING_COMPLETE':
      return loadingComplete('rules')
    case 'MAILCHIMP_DELETE_RULE':
      return deleteRule(state, payload)
    case 'MAILCHIMP_DELETE_RULE_SUCCESS':
      return deleteRuleSuccess(state, payload)
    case 'MAILCHIMP_DELETE_RULE_ERROR':
      return deleteRuleError(state, payload)
    case 'MAILCHIMP_CLEAR_SELECTED_TAGS':
      return clearSelectedTags(state)
    case 'MAILCHIMP_SELECT_RULE':
      return selectRule(state, payload)
    case 'MAILCHIMP_OPEN_RULE_EDITOR':
      return openRuleEditor(state)
    case 'MAILCHIMP_CLOSE_RULE_EDITOR':
      return closeRuleEditor(state)
    case 'MAILCHIMP_RULE_EDITOR_SAVING':
      return ruleEditorIsSaving(state, payload)
    case 'MAILCHIMP_RULE_EDITOR_SAVING_SUCCESS':
      return ruleEditorSavingSuccess(state, payload)
    case 'MAILCHIMP_RULE_EDITOR_SAVING_ERROR':
      return ruleEditorSavingError(state, payload)
    case 'MAILCHIMP_SET_EVENT_TYPE_ERRORS':
      return setEventTypeErrors(state, payload)
    case 'MAILCHIMP_SET_AUDIENCE_ERRORS':
      return setAudienceErrors(state, payload)
    case 'MAILCHIMP_RULE_EDITOR_LOADING':
      return ruleEditorIsLoading(state)
    case 'MAILCHIMP_RULE_EDITOR_LOADING_COMPLETE':
      return ruleEditorLoadingComplete(state)
    case 'MAILCHIMP_FETCH_ACCOUNT':
      return startAccountLoading(state)
    case 'MAILCHIMP_FETCH_ACCOUNT_COMPLETE':
      return stopAccountLoading(state)
    case 'MAILCHIMP_FETCH_ACCOUNT_SUCCESS':
      return accountLoaded(state, payload)
    default:
      return { ...initialState, ...state }
  }
}
